import { ApplicationSelectData, ApplicationType, AuthenticatedContext } from '@sec-loans-ui/common/types'
import {
  clearIfpWizardMachineSessionStorage,
  WizardContext as IfpLoansWizardContext,
} from '@sec-loans-ui/ifp-loans/wizard/wizardMachine'
import { WizardContext as LoanOfferWizardContext } from '@sec-loans-ui/loan-offer/wizard/wizardMachine'
import {
  getIfpLoanByApplicationId,
  getIfpLoanFromUserByUuid,
  getLoanOfferId,
  getSecLoansEntry,
} from '@sec-loans-ui/utils/helpers'
import { User } from '@sec-loans-ui/wizard/types'
import {
  clearSecLoansWizardMachineSessionStorage,
  initialSecLoansWizardContext,
  WizardContext as SecLoansWizardContext,
} from '@sec-loans-ui/wizard/wizardMachine'
import { assertValue } from '@shared/helpers'
import {
  ApplicationsMetaData,
  IfpApplicationState,
  SecLoansCreateApplicationResponse,
  SecLoansEntry,
  SecLoansParam,
  SecLoansUserInfoResponse,
} from '@shared/sec-loans'
import { ConfigResponse } from '@shared/types'
import { AuthenticatedEvent } from '@ui-common/state'
import { AuthenticationCancelledEvent } from '@ui-common/steps/AuthenticateStep/types'
import { GetDataDoneEvent } from '@ui-common/steps/GetDataStep/getDataMachine'
import { ExitWizardEvent, NextStepEvent as CommonNextStepEvent } from '@ui-common/types'
import { isComingBackFromAuthentication, isComingBackFromSigning } from '@ui-common/utils/authentication'
import { toNumber } from '@ui-common/utils/helpers'
import { assign, setup, StateFrom } from 'xstate'

import { OnCreatedIfpApplicationData } from '../../ifp-loans/types'
import { resolveInitialLoanOfferId } from '../../loan-offer/helpers'
import { OnAutomaticOfferData } from '../../loan-offer/types'

export interface WizardContext extends Partial<AuthenticatedContext>, ConfigResponse<SecLoansParam> {
  user?: User
  applications?: ApplicationsMetaData
  shouldLogoutAfterAuthentication?: boolean
  ifpLoansEntryContext?: Partial<IfpLoansWizardContext>
  loanOfferEntryContext?: Partial<LoanOfferWizardContext>
  secLoansEntryContext?: Partial<SecLoansWizardContext>
}

export enum WizardStep {
  Initialize = 'initialize',
  Entrance = 'StepEntrance',
  Authenticate = 'authenticate',
  GetUserInfo = 'get-user-info',
  GetAllApplications = 'StepGetAllApplications',
  LoanCatalog = 'StepLoanCatalog',
  Assistance = 'StepAssistance',
  GoToIfpLoans = 'StepGoToIfpLoans',
  GoToLoanOffer = 'StepGoToLoanOffer',
  GoToSecLoans = 'StepGoToSecLoans',
  Aborted = 'StepAborted',
}

const initialContext: WizardContext = {
  secLoansEntryContext: initialSecLoansWizardContext,
}

export const shouldHydrateState = (state: StateFrom<WizardMachineType>): boolean => {
  const value =
    (state?.value === WizardStep.Authenticate && isComingBackFromAuthentication()) ||
    (isComingBackFromSigning() && state?.value === WizardStep.GoToIfpLoans)

  return value
}

type SelectAppEvent = {
  type: 'SELECT_APP'
} & ApplicationSelectData

type CreatedSecLoansEvent = {
  type: 'CREATED_SEC_LOANS_APPLICATION'
  data: SecLoansCreateApplicationResponse
}

type CreatedIfpLoansEvent = {
  type: 'CREATED_IFP_LOANS_APPLICATION'
  data: OnCreatedIfpApplicationData
}

type GoToGetAllApplications = { type: 'GO_TO_GET_ALL_APPLICATIONS' }
type GoToAssistanceEvent = { type: 'GO_TO_ASSISTANCE' }
type GoToLoanOfferEvent = { type: 'GO_TO_LOAN_OFFER'; data: OnAutomaticOfferData }
type NextStepEvent = CommonNextStepEvent<unknown>
type WizardEvent =
  | GoToGetAllApplications
  | GoToAssistanceEvent
  | SelectAppEvent
  | CreatedSecLoansEvent
  | CreatedIfpLoansEvent
  | NextStepEvent
  | AuthenticatedEvent
  | AuthenticationCancelledEvent
  | GetDataDoneEvent<User>
  | GetDataDoneEvent<ApplicationsMetaData>
  | GoToLoanOfferEvent
  | ExitWizardEvent

export type WizardMachineType = ReturnType<typeof createWizardMachine>
// return type is derived from setup fn and used in WizardMachineType
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createWizardMachine = (authenticatedContext?: AuthenticatedContext) =>
  setup({
    types: {
      context: {} as WizardContext,
      events: {} as WizardEvent,
    },
    actions: {
      saveInitialData: assign(({ context }, config: ConfigResponse<SecLoansParam>) => {
        const minimumHousingLoanAmount = toNumber(config.params?.MINIMUM_HOUSING_LOAN_AMOUNT)
        const minimumPurchaseLoanAmount = toNumber(config.params?.MINIMUM_PURCHASE_LOAN_AMOUNT)
        return {
          ...config,
          ifpLoansEntryContext: {
            ...context.ifpLoansEntryContext,
            ...config,
          },
          secLoansEntryContext: {
            ...context.secLoansEntryContext,
            ...config,
            validationParams: {
              minimumHousingLoanAmount: assertValue(minimumHousingLoanAmount, 'minimumHousingLoanAmount'),
              minimumPurchaseLoanAmount: assertValue(minimumPurchaseLoanAmount, 'minimumPurchaseLoanAmount'),
            },
          },
          loanOfferEntryContext: {
            ...context.loanOfferEntryContext,
            ...config,
          },
        }
      }),
      saveAuthenticationData: assign(({ context }, authToken: string) => {
        return {
          authToken,
          ifpLoansEntryContext: {
            ...context.ifpLoansEntryContext,
            authToken,
          },
          secLoansEntryContext: {
            ...context.secLoansEntryContext,
            authToken,
          },
          loanOfferEntryContext: {
            ...context.loanOfferEntryContext,
            authToken,
          },
        }
      }),
      storeBasicInfo: assign(({ context }, user: User) => {
        return {
          user,
          applications: {
            ifpApplicationsMetadata: user.ifpApplications,
            loanApplicationsMetadata: user.loanApplications,
            loanOffersMetadata: user.loanOffers,
          },
          ifpLoansEntryContext: {
            ...context.ifpLoansEntryContext,
            user,
          },
          loanOfferEntryContext: {
            ...context.loanOfferEntryContext,
            loanOfferId: resolveInitialLoanOfferId(user),
            user,
          },
          secLoansEntryContext: {
            ...context.secLoansEntryContext,
            user,
          },
        }
      }),
      storeApplicationDetails: assign(({ context }, applications: ApplicationsMetaData) => {
        return {
          ...context,
          applications,
          user: {
            ...assertValue(context.user, 'context.user'),
            ifpApplications: applications.ifpApplicationsMetadata,
            loanApplications: applications.loanApplicationsMetadata,
            loanOffers: applications.loanOffersMetadata,
          },
        }
      }),
      storeCreatedSecLoansApplication: assign(({ context }, data: SecLoansCreateApplicationResponse) => {
        return {
          secLoansEntryContext: {
            ...context.secLoansEntryContext,
            isNewApplication: true,
            ...data,
            applicationMetadata: { ...data },
            applicationData: data,
            applicationType: data.applicationType,
            applicationPurpose: data.applicationPurpose,
            applicationId: data.applicationId,
          },
        }
      }),
      storeCreatedIfpApplication: assign(({ context }, { applicationId, loanUuid }: OnCreatedIfpApplicationData) => {
        return {
          ifpLoansEntryContext: {
            ...context.ifpLoansEntryContext,
            isPrimaryApplicant: true,
            isNewApplication: true,
            applicationId,
            applicationState: IfpApplicationState.Draft,
            loan: getIfpLoanFromUserByUuid(assertValue(context.user, 'context.user'), loanUuid),
          },
        }
      }),
      storeApplicationSelection: assign(({ context }, event: SelectAppEvent) => {
        if (event.applicationType === ApplicationType.InstalmentFreePeriodApplication) {
          return {
            ifpLoansEntryContext: {
              ...context.ifpLoansEntryContext,
              applicationId: event.applicationId,
              applicationState: event.applicationState,
              loan: getIfpLoanByApplicationId(
                assertValue(context.user, 'context.user'),
                assertValue(context.applications, 'context.applications'),
                event.applicationId,
              ),
              isNewApplication: false,
            } satisfies Partial<IfpLoansWizardContext>,
          }
        }

        if (event.applicationType === ApplicationType.LoanApplication) {
          return {
            secLoansEntryContext: {
              ...context.secLoansEntryContext,
              isNewApplication: false,
              applicationId: event.applicationId,
              ...event.data,
            } satisfies Partial<SecLoansWizardContext>,
          }
        }

        if (event.applicationType === ApplicationType.LoanOffer) {
          return {
            loanOfferEntryContext: {
              ...context.loanOfferEntryContext,
              user: context.user,
              loanOfferId: event.id,
              loanOffer: event.loanOffer,
            },
          }
        }

        const exhaustiveCheck: never = event
        throw new Error(`Unsupported event: ${exhaustiveCheck}`)
      }),
    },
    guards: {
      shouldGoLoanOffer: (_, data: User) => {
        if (data.loanOffers.some((loanOffer) => loanOffer.id === getLoanOfferId())) {
          return true
        }
        const validEntry = getSecLoansEntry() === SecLoansEntry.LoanOffer
        return validEntry && !!resolveInitialLoanOfferId(data)
      },
      shouldGoToCatalog: (_, data: User) => {
        const entry = getSecLoansEntry()
        if ([SecLoansEntry.Catalog, SecLoansEntry.LoanOffer].includes(entry)) {
          return true
        }
        const { loanOffers, ifpApplications, loanApplications } = data
        return (
          entry === SecLoansEntry.Default &&
          (loanOffers.length > 0 || ifpApplications.length > 0 || loanApplications.length > 0)
        )
      },
      isSelectedAppSecLoans: (_, applicationType: ApplicationType) =>
        applicationType === ApplicationType.LoanApplication,
      isSelectedAppIfpLoans: (_, applicationType: ApplicationType) =>
        applicationType === ApplicationType.InstalmentFreePeriodApplication,
      isSelectedAppLoanOffer: (_, applicationType: ApplicationType) => applicationType === ApplicationType.LoanOffer,
      shouldPassCbsToken: ({ context }) => {
        return !!context.exitUrls?.cbsTokenParameter
      },
      shouldLogoutAfterAuthentication: ({ context }) => {
        return !!context.shouldLogoutAfterAuthentication
      },
    },
  }).createMachine({
    id: 'rootWizardMachine',
    initial: authenticatedContext ? WizardStep.GetUserInfo : WizardStep.Initialize,
    context: authenticatedContext ? { ...initialContext, ...authenticatedContext } : initialContext,
    on: { EXIT_WIZARD: { target: `.${WizardStep.Aborted}` } },
    states: {
      [WizardStep.Initialize]: {
        id: WizardStep.Initialize,
        on: {
          NEXT_STEP: {
            target: WizardStep.Entrance,
            actions: {
              type: 'saveInitialData',
              params: ({ event }) => (event as CommonNextStepEvent<ConfigResponse<SecLoansParam>>).data,
            },
          },
        },
      },
      [WizardStep.Entrance]: {
        id: WizardStep.Entrance,
        on: {
          NEXT_STEP: { target: WizardStep.Authenticate },
          EXIT_WIZARD: [
            {
              guard: { type: 'shouldPassCbsToken' },
              target: WizardStep.Authenticate,
              actions: assign(() => ({
                shouldLogoutAfterAuthentication: true,
              })),
            },
            {
              target: WizardStep.Aborted,
            },
          ],
        },
      },
      [WizardStep.Authenticate]: {
        id: WizardStep.Authenticate,
        on: {
          AUTHENTICATION_DONE: [
            {
              guard: { type: 'shouldLogoutAfterAuthentication' },
              target: WizardStep.Aborted,
              actions: { type: 'saveAuthenticationData', params: ({ event }) => event.data.authToken },
            },
            {
              target: WizardStep.GetUserInfo,
              actions: { type: 'saveAuthenticationData', params: ({ event }) => event.data.authToken },
            },
          ],
          AUTHENTICATION_CANCELLED: {
            target: WizardStep.Entrance,
          },
        },
      },
      [WizardStep.GetUserInfo]: {
        id: WizardStep.GetUserInfo,
        on: {
          GET_DATA_DONE: [
            {
              target: WizardStep.GoToLoanOffer,
              guard: { type: 'shouldGoLoanOffer', params: ({ event }) => event.data as SecLoansUserInfoResponse },
              actions: {
                type: 'storeBasicInfo',
                params: ({ event }) => event.data as SecLoansUserInfoResponse,
              },
            },
            {
              target: WizardStep.LoanCatalog,
              guard: { type: 'shouldGoToCatalog', params: ({ event }) => event.data as SecLoansUserInfoResponse },
              actions: {
                type: 'storeBasicInfo',
                params: ({ event }) => event.data as SecLoansUserInfoResponse,
              },
            },
            {
              target: WizardStep.Assistance,
              actions: {
                type: 'storeBasicInfo',
                params: ({ event }) => event.data as SecLoansUserInfoResponse,
              },
            },
          ],
        },
      },
      [WizardStep.GetAllApplications]: {
        id: WizardStep.GetAllApplications,
        on: {
          GET_DATA_DONE: {
            target: WizardStep.LoanCatalog,
            actions: {
              type: 'storeApplicationDetails',
              params: ({ event }) => event.data as ApplicationsMetaData,
            },
          },
        },
      },
      [WizardStep.LoanCatalog]: {
        id: WizardStep.LoanCatalog,
        on: {
          GO_TO_ASSISTANCE: {
            target: WizardStep.Assistance,
          },
          SELECT_APP: [
            {
              guard: { type: 'isSelectedAppSecLoans', params: ({ event }) => event.applicationType },
              target: WizardStep.GoToSecLoans,
              actions: {
                type: 'storeApplicationSelection',
                params: ({ event }) => assertValue(event, 'event'),
              },
            },
            {
              guard: { type: 'isSelectedAppIfpLoans', params: ({ event }) => event.applicationType },
              target: WizardStep.GoToIfpLoans,
              actions: {
                type: 'storeApplicationSelection',
                params: ({ event }) => assertValue(event, 'event'),
              },
            },
            {
              guard: { type: 'isSelectedAppLoanOffer', params: ({ event }) => event.applicationType },
              target: WizardStep.GoToLoanOffer,
              actions: {
                type: 'storeApplicationSelection',
                params: ({ event }) => assertValue(event, 'event'),
              },
            },
          ],
        },
      },
      [WizardStep.Assistance]: {
        id: WizardStep.Assistance,
        on: {
          GO_TO_GET_ALL_APPLICATIONS: {
            target: WizardStep.GetAllApplications,
          },
          CREATED_SEC_LOANS_APPLICATION: {
            actions: {
              type: 'storeCreatedSecLoansApplication',
              params: ({ event }) => assertValue(event.data, 'event.data'),
            },
            target: WizardStep.GoToSecLoans,
          },
          CREATED_IFP_LOANS_APPLICATION: {
            actions: {
              type: 'storeCreatedIfpApplication',
              params: ({ event }) => assertValue(event.data, 'event.data'),
            },
            target: WizardStep.GoToIfpLoans,
          },
        },
      },
      [WizardStep.GoToIfpLoans]: {
        id: WizardStep.GoToIfpLoans,
        entry: () => {
          // At this point we want to always make sure ifp loans wizard machine doesn't hydrate from session storage
          clearIfpWizardMachineSessionStorage()
        },
        on: {
          GO_TO_GET_ALL_APPLICATIONS: {
            target: WizardStep.GetAllApplications,
          },
          GO_TO_ASSISTANCE: {
            target: WizardStep.Assistance,
          },
        },
      },
      [WizardStep.GoToLoanOffer]: {
        id: WizardStep.GoToLoanOffer,
        on: {
          GO_TO_GET_ALL_APPLICATIONS: {
            target: WizardStep.GetAllApplications,
          },
          GO_TO_ASSISTANCE: {
            target: WizardStep.Assistance,
          },
        },
      },
      [WizardStep.GoToSecLoans]: {
        id: WizardStep.GoToSecLoans,
        entry: () => {
          clearSecLoansWizardMachineSessionStorage()
        },
        on: {
          GO_TO_GET_ALL_APPLICATIONS: {
            target: WizardStep.GetAllApplications,
          },
          GO_TO_ASSISTANCE: {
            target: WizardStep.Assistance,
          },
          GO_TO_LOAN_OFFER: {
            target: WizardStep.GoToLoanOffer,
            actions: {
              type: 'storeApplicationSelection',
              params: ({ event }) => ({
                applicationType: ApplicationType.LoanOffer,
                type: 'SELECT_APP',
                id: event.data.loanOffer.id,
                loanOffer: event.data.loanOffer,
              }),
            },
          },
        },
      },
      [WizardStep.Aborted]: {
        id: WizardStep.Aborted,
        type: 'final',
      },
    },
  })
